/**
 * auth.ts — Pinia store for authentication and key session state.
 *
 * This store is the single source of truth for:
 *   • Whether the user is authenticated (access token present in memory)
 *   • The access token (JWT, 15 min, memory-only — never written to disk)
 *   • The refresh token (UUID, 30 days)
 *       - Web: HttpOnly cookie managed by the server (not stored here)
 *       - Desktop/Android: stored in app-private storage (retrieved externally, passed in)
 *   • Current user ID and workspace ID
 *   • Whether the key session is unlocked (MEK in memory)
 *
 * It orchestrates calls to authService (network) and keyManager (crypto).
 * Components must NEVER call authService or keyManager directly.
 *
 * Token refresh strategy:
 *   The access token (15 min JWT) is refreshed proactively via a background
 *   interval started on login. On web the refresh token lives in an HttpOnly
 *   cookie so no client-side storage is needed. On desktop/Android the raw
 *   refresh token string is stored externally (app-private storage) and passed to
 *   `initFromStoredToken` on app start.
 */

import { getPlatformBridge } from '../services/platformBridge'
import { commitCredentials, recoverCredentialTransaction } from '../services/credentialTransaction'
import { throwIfAborted, withRequestDeadline } from '../services/requestDeadline'
import { syncLog } from '../services/syncLog'
import { defineStore } from 'pinia'
import { ref, computed, watch, onScopeDispose } from 'vue'
import {
  AuthHttpError,
  login,
  logout,
  refreshAccessToken,
  resetPasswordWithRecoveryCode,
  requestUsernameReminder as requestUsernameReminderRequest,
  changePassword as changePasswordRequest,
  changeUsername as changeUsernameRequest,
  rotateRecoveryCode as rotateRecoveryCodeRequest,
  listSessions,
  revokeSession as revokeSessionRequest,
  renameSession as renameSessionRequest,
  deleteAccount as deleteAccountRequest,
  type DeviceSession,
} from '../services/authService'
import { prepareLocalSessionClose } from '../services/localSession'
import { keyManager, verifyKeyCanary } from '../services/keyManager'
import { syncEngine } from '../services/syncEngine'
import { secureStorage } from '../services/secureStorage'
import { useSettingsStore } from './settings'
import { applySettingsPull } from '../services/toolDataSync'
import { useCorpusStore } from './corpus'
import { prepareEntitlement, persistEntitlement, readOfflineAccountId, activateEntitlement, readOfflineEntitlement } from '../services/entitlementService'
import { wipeLocalData } from '../services/localWipe'
import { OtherAccountWritingError } from '../services/otherAccountWriting'
import { reportSyncCheckpoint, type SyncCheckpointStatus } from '../services/deviceCheckpoint'

// ── Types ─────────────────────────────────────────────────────────────────────

export type KeySessionState = 'restoring' | 'unlocked' | 'locked' | 'error'

// ── Store ─────────────────────────────────────────────────────────────────────

export const useAuthStore = defineStore('auth', () => {
  // ── State ──────────────────────────────────────────────────────────────────

  /** JWT access token — memory-only, never persisted. */
  const accessToken = ref<string | null>(null)

  /**
   * Raw refresh token string.
   * On web this is null or in memory — the HttpOnly cookie is sent automatically by the browser.
   * On desktop/Android this holds the raw token stored in secureStorage.
   */
  const refreshToken = ref<string | null>(null)

  const currentSessionId = ref<string | null>(null)
  const sessions = ref<DeviceSession[]>([])
  const loadingSessions = ref(false)

  const userId = ref<string | null>(null)
  const workspaceId = ref<string | null>(null)
  /** Internal name avoids colliding with the `username` parameter used throughout this file's actions. Exposed externally as `username` — see the store's return statement. */
  const currentUsername = ref<string | null>(
    typeof window !== 'undefined' ? localStorage.getItem('superelectric_ink_username_v1') : null
  )

  /** Explicit key-session state distinct from network auth state. */
  const keySessionState = ref<KeySessionState>(keyManager.isUnlocked() ? 'unlocked' : 'locked')

  /** Reason local keys could not unlock; cloud rejection is tracked separately. */
  const lockedReason = ref<string | null>(null)

  /** Why cloud authentication stopped; independent of local key access. */
  const cloudSessionReason = ref<string | null>(secureStorage.getCloudState())

  /** Remote deletion disables cloud access and preserves permanent local access. */
  const accountDeleted = ref(secureStorage.getCloudState() === 'account_deleted')
  const locallyLocked = ref(secureStorage.isLocallyLocked())

  /**
   * True while Argon2id derivation or network calls are in flight.
   * Argon2id itself now runs off the main thread (keyManager.ts dispatches
   * it to cryptoWorker.ts), so this genuinely reflects background work —
   * the UI thread stays responsive while it's true, rather than merely
   * showing a spinner over a blocked thread.
   */
  const loading = ref(false)

  /** Last error message — cleared at the start of each auth action. */
  const error = ref<string | null>(null)

  /** Tracks the setInterval ID for the proactive token refresh. */
  let refreshInterval: ReturnType<typeof setInterval> | null = null

  /** Deduplicates interval, resume, and 401-triggered refresh attempts. */
  let tokenRefreshPromise: Promise<string | null> | null = null

  /**
   * Set when the server refused the stored refresh token, so automatic
   * reconnects stop retrying it. Cleared whenever a new session is established.
   */
  let storedSessionRejected = !!secureStorage.getCloudState()

  let sessionRequests = new AbortController()
  let sessionRestorePromise: Promise<boolean> | null = null
  let renewalPromise: Promise<void> | null = null

  function cancelSessionRequests(): void {
    sessionRequests.abort()
    sessionRequests = new AbortController()
    sessionRestorePromise = null
    tokenRefreshPromise = null
    renewalPromise = null
  }

  function renewCertificate(token: string, sessionId: string | undefined, accountId: string | undefined, signal: AbortSignal): void {
    if (renewalPromise || signal.aborted) return
    const task = activateEntitlement(token, sessionId, { signal, accountId }).catch((err) => {
      if (signal.aborted) return
      console.warn('[AuthStore] Entitlement renewal failed; existing offline certificate remains valid:', err)
      syncLog.record({ direction: 'system', level: 'warn', kind: 'license-renewal', message: 'License renewal could not reach or verify the server. The existing local license remains available; renewal will retry.' })
    }).finally(() => {
      if (renewalPromise === task) renewalPromise = null
    })
    renewalPromise = task
  }

  // ── Computed ───────────────────────────────────────────────────────────────

  /** True when an access token is present in memory. */
  const isAuthenticated = computed(() => !!accessToken.value)

  /** True when the MEK and ECDH private key are held in memory. */
  const isKeySessionUnlocked = computed(() => keySessionState.value === 'unlocked')

  // Keep keySessionState strictly in sync with keyManager in-memory crypto state
  keyManager.onUnlockChange((unlocked) => {
    keySessionState.value = unlocked ? 'unlocked' : 'locked'
  })

  // ── Actions ────────────────────────────────────────────────────────────────

  /**
   * Logs in with username and password.
   *
   * Argon2id (~1-3 s) runs off the main thread via cryptoWorker.ts — `loading`
   * reflects genuine background work here, not a blocking call dressed up
   * with a spinner.
   * On success:
   *   • `accessToken` and `userId` are set in memory.
   *   • `refreshToken` is saved to secureStorage.
   *   • keyManager holds the MEK and ECDH private key in memory.
   *   • syncEngine is initialized with session details.
   *   • Proactive token refresh starts.
   *   • Remote tree & settings are automatically pulled down.
   */
  async function signIn(
    username: string,
    password: string,
    deviceLabel?: string,
  ): Promise<void> {
    cancelSessionRequests()
    const signal = sessionRequests.signal
    loading.value = true
    error.value = null
    let loggedIn: { accessToken: string; refreshToken: string } | null = null
    try {
      const session = await login(username, password, deviceLabel, signal)
      throwIfAborted(signal)
      loggedIn = session
      await _applySession(session.accessToken, session.refreshToken, session.userId, session.workspaceId, session.sessionId, session.preparedKeys, username, deviceLabel)
    } catch (e) {
      if (signal.aborted) throw e
      // Prepared keys have never replaced the working cache. Revoke only the
      // new server session when activation or the durable commit fails.
      if (loggedIn) await logout(loggedIn.accessToken, loggedIn.refreshToken).catch(() => {})
      error.value = e instanceof Error ? e.message : String(e)
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Reconciles remote tree and settings from cloud, applying changes to local stores
   * and flushing pending mutations. Launch can ask for this from several paths
   * at once (session restore, key restore, bootstrap); they share one run.
   */
  let syncInFlight: Promise<void> | null = null
  function syncWithCloud(): Promise<void> {
    if (!syncInFlight) {
      syncInFlight = runSyncWithCloud().finally(() => { syncInFlight = null })
    }
    return syncInFlight
  }

  async function runSyncWithCloud(): Promise<void> {
    if (!accessToken.value || !keyManager.isUnlocked() || syncEngine.syncStopped.value || syncEngine.syncLapsed.value) return

    let checkpointStatus: SyncCheckpointStatus = 'ok'

    // The tree, settings and outbox are independent. A failure in one (e.g. a
    // server error on settings) must not stop the others; above all, it must
    // not keep queued local changes from being uploaded.
    const [treeResult, settingsResult] = await Promise.allSettled([
      syncEngine.pullTree(),
      syncEngine.pullSettings(),
    ])

    try {
      if (treeResult.status === 'rejected') throw treeResult.reason
      const treeNodes = treeResult.value
      const corpusStore = useCorpusStore()
      await corpusStore.applyRemotePurges((treeNodes as any)?.purgedIds ?? [], treeNodes ?? [])
      if (treeNodes && treeNodes.length > 0) {
        corpusStore.applyRemoteTree(treeNodes, (treeNodes as any).isDelta)
      }
      syncEngine.commitTreePull(treeNodes)
    } catch (err) {
      checkpointStatus = 'error'
      console.warn('[AuthStore] Tree sync failed:', err)
    }

    try {
      if (settingsResult.status === 'rejected') throw settingsResult.reason
      await applySettingsPull(settingsResult.value)
    } catch (err) {
      checkpointStatus = 'error'
      console.warn('[AuthStore] Settings sync failed:', err)
    }

    try {
      await syncEngine.flushOutbox()
    } catch (err) {
      checkpointStatus = 'error'
      console.warn('[AuthStore] Outbox flush failed:', err)
    }

    if (await syncEngine.refreshPendingCount() > 0) checkpointStatus = 'error'
    void reportSyncCheckpoint(accessToken.value, checkpointStatus)
  }

  /**
   * Stops cloud sync on this device without signing out. The account session
   * and entitlement keep renewing, so the app stays usable; nothing is sent to
   * or pulled from the cloud until resumeSync().
   */
  function stopSync(): void {
    syncEngine.setSyncStopped(true)
  }

  /** Resumes cloud sync and catches up in both directions. */
  async function resumeSync(): Promise<void> {
    syncEngine.setSyncStopped(false)
    await syncWithCloud()
  }

  /** Explicit cloud sign-out retains local keys, license, writing and pending edits. */
  async function signOut(): Promise<void> {
    cancelSessionRequests()
    const token = accessToken.value
    const refresh = refreshToken.value
    loading.value = true
    try {
      await recoverCredentialTransaction()
      // Persist intent before disconnecting, including when the server cannot be reached.
      const reason = accountDeleted.value ? 'account_deleted' : 'signed_out'
      secureStorage.setCloudState(reason)
      await _disconnectCloudSession(reason)
      error.value = null
      // Local access is already disconnected. Finish bounded server revocation
      // before another UI sign-in can replace its cookie.
      if (token) await logout(token, refresh ?? undefined).catch(() => {})
    } finally { loading.value = false }
  }

  /** Lock the writing UI locally; unlock uses this device's last cached password. */
  async function lockKeySession(): Promise<void> {
    const data = await getPlatformBridge()?.readKeyStore?.()
    if (!data?.offlineUnlock) throw new Error('Sign in once online to enable offline password unlock before locking this device.')
    const resume = await prepareLocalSessionClose()
    try {
      if (!await keyManager.saveToLocalCache()) throw new Error('Local keys could not be saved. Free storage space before locking.')
      await signOut()
      secureStorage.setLocallyLocked(true)
      locallyLocked.value = true
      keyManager.clear()
      keySessionState.value = 'locked'
      lockedReason.value = 'local_lock'
    } finally { resume() }
  }

  /** Deletes cloud account/data while retaining permanent local access and writing. */
  async function deleteCloudAccount(currentPassword: string, confirmationUsername: string): Promise<void> {
    if (!accessToken.value) throw new Error('Not signed in.')
    loading.value = true
    error.value = null
    try {
      await deleteAccountRequest(currentPassword, confirmationUsername, accessToken.value)
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
      throw e
    } finally {
      loading.value = false
    }
    await _markAccountDeleted()
  }

  /** Explicit removal only, after the UI offers export and confirms data loss. */
  async function removeLocalData(): Promise<void> {
    let resume = () => {}
    try {
      resume = await prepareLocalSessionClose()
      await recoverCredentialTransaction()
      cancelSessionRequests()
      storedSessionRejected = true
      syncEngine.disconnectAllDocuments()
      _stopRefreshInterval()
      accessToken.value = null
      refreshToken.value = null
      syncEngine.init({ authToken: null, workspaceId: null, userId: null })
      await wipeLocalData()
      window.location.reload()
    } catch (cause) {
      error.value = `Local removal failed: ${cause instanceof Error ? cause.message : String(cause)}. Free storage space or allow storage, then retry removal before changing accounts.`
      throw cause
    } finally { resume() }
  }

  /** Server deletion never removes this device's permanent license or writing. */
  async function _markAccountDeleted(): Promise<void> {
    accountDeleted.value = true
    try { secureStorage.setCloudState('account_deleted') } catch { reportCredentialStorageFailure() }
    await _disconnectCloudSession('account_deleted')
    error.value = 'This cloud account was deleted. Your local writing and license remain available; cloud sync is disabled.'
  }

  /**
   * Restores a session from a stored refresh token (desktop/Android on cold start).
   */
  function restoreSession(storedRefreshToken?: string): Promise<boolean> {
    if (sessionRestorePromise) return sessionRestorePromise
    const task = performSessionRestore(storedRefreshToken, sessionRequests.signal).finally(() => {
      if (sessionRestorePromise === task) sessionRestorePromise = null
    })
    sessionRestorePromise = task
    return task
  }

  async function performSessionRestore(storedRefreshToken: string | undefined, signal: AbortSignal): Promise<boolean> {
    try {
      await recoverCredentialTransaction()
      if (secureStorage.getCloudState() || locallyLocked.value || secureStorage.isLocallyLocked()) return false
      const tokenToUse = storedRefreshToken || (await secureStorage.getRefreshToken()) || undefined
      if (!tokenToUse) {
        return false
      }
      throwIfAborted(signal)
      const res = await refreshAccessToken(tokenToUse, signal)
      throwIfAborted(signal)
      if (!res || typeof res.accessToken !== 'string' || !res.accessToken.trim()) return false
      // An existing account-matched certificate is sufficient for local use.
      // First activation still has to obtain a verified license from the server.
      const owner = await secureStorage.getLocalAccountId() ?? await readOfflineAccountId()
      const cachedKeyAccount = (await getPlatformBridge()?.readKeyStore?.())?.accountId
      if (cachedKeyAccount && cachedKeyAccount !== res.userId) throw new AuthHttpError('Encryption keys belong to another account', 403, 'account_mismatch')
      if (owner && res.userId !== owner) throw new AuthHttpError('Stored cloud session belongs to another account', 403, 'account_mismatch')
      const cachedLicense = res.userId ? await readOfflineEntitlement(res.userId) : null
      throwIfAborted(signal)
      if (!cachedLicense) await activateEntitlement(res.accessToken, res.sessionId, { signal, accountId: res.userId })
      throwIfAborted(signal)
      const restoredSessionId = res.sessionId ?? await secureStorage.getSessionId()
      const restoredUsername = currentUsername.value || await secureStorage.getUsername()
      if (res.sessionId) await secureStorage.setSessionId(res.sessionId)
      throwIfAborted(signal)
      storedSessionRejected = false
      cloudSessionReason.value = null
      accessToken.value = res.accessToken
      lockedReason.value = null
      refreshToken.value = tokenToUse
      if (res.userId) userId.value = res.userId
      if (res.workspaceId) workspaceId.value = res.workspaceId
      currentSessionId.value = restoredSessionId
      currentUsername.value = restoredUsername

      syncEngine.init({
        authToken: res.accessToken,
        workspaceId: res.workspaceId || workspaceId.value,
        userId: res.userId || userId.value,
      })

      _startRefreshInterval()
      if (cachedLicense) renewCertificate(res.accessToken, res.sessionId, res.userId, signal)

      if (keyManager.isUnlocked()) {
        syncWithCloud().catch((err) => {
          console.warn('[AuthStore] Failed to sync on restoreSession:', err)
        })
      } else {
        const settingsStore = useSettingsStore()
        settingsStore.syncSettingsWithCloud().catch((err) => {
          console.warn('[AuthStore] Failed to sync settings on restoreSession:', err)
        })
      }

      return true
    } catch (e: any) {
      if (signal.aborted) return false
      if (isCloudSessionRejection(e)) {
        if (e.reason === 'account_deleted') {
          await _markAccountDeleted()
        } else {
          await _disconnectCloudSession(e.reason)
        }
      }
      return false
    }
  }

  /**
   * Restores the Key Session from local keystore / cache (warm restore).
   * Runs independently of network authentication.
   */
  async function restoreKeySession(): Promise<boolean> {
    const signal = sessionRequests.signal
    if (locallyLocked.value || secureStorage.isLocallyLocked()) {
      keySessionState.value = 'locked'
      lockedReason.value = 'local_lock'
      return false
    }
    keySessionState.value = 'restoring'
    try {
      // A valid certificate is a permanent local license; whether sync is
      // still paid is applied to the sync engine as it is read.
      await recoverCredentialTransaction()
      // Recovery may have restored a lock from an interrupted sign-in journal.
      if (secureStorage.isLocallyLocked()) {
        locallyLocked.value = true
        keySessionState.value = 'locked'
        lockedReason.value = 'local_lock'
        return false
      }
      const entitlement = await readOfflineEntitlement()
      if (signal.aborted) return false
      if (!entitlement) {
        keySessionState.value = 'locked'
        lockedReason.value = 'no_license'
        return false
      }
      const account = await readOfflineAccountId()
      const restored = !!account && await keyManager.restoreFromLocalCache(signal, account)
      if (restored && account) {
        // Legacy caches acquire an owner only after both signature and key canary pass.
        await secureStorage.setLocalAccountId(account).catch(() => {
          syncLog.record({ direction: 'system', level: 'error', kind: 'credential-storage', message: 'The local account association could not be saved. Free storage space and restart.' })
        })
        userId.value = account
      }
      if (signal.aborted) return false
      keySessionState.value = restored ? 'unlocked' : 'locked'
      if (restored && isAuthenticated.value) {
        syncWithCloud().catch((err) => {
          console.warn('[AuthStore] Failed to sync on restoreKeySession:', err)
        })
      }
      return restored
    } catch (err) {
      if (signal.aborted) return false
      console.warn('[AuthStore] Key session warm restore error:', err)
      keySessionState.value = 'error'
      return false
    }
  }

  /**
   * Restores local keys first, then restores cloud access without blocking local writing.
   */
  async function bootstrapSession(storedRefreshToken?: string): Promise<{ sessionRestored: boolean; keysRestored: boolean }> {
    const signal = sessionRequests.signal
    let keysRestored = await restoreKeySession()
    if (signal.aborted) return { sessionRestored: false, keysRestored: false }
    const cloudRestore = restoreSession(storedRefreshToken)
    if (keysRestored) {
      // Opening local writing never waits for auth or certificate renewal.
      void cloudRestore
      return { sessionRestored: isAuthenticated.value, keysRestored: true }
    }
    const sessionRestored = await cloudRestore
    if (sessionRestored && !signal.aborted) keysRestored = await restoreKeySession()
    if (signal.aborted && !accountDeleted.value) return { sessionRestored: false, keysRestored: false }

    return { sessionRestored, keysRestored }
  }

  /** Local password unlock: no auth, activation, or sync request is required. */
  async function unlockKeySession(password: string): Promise<void> {
    cancelSessionRequests()
    const signal = sessionRequests.signal
    loading.value = true
    error.value = null
    try {
      await recoverCredentialTransaction()
      const account = await readOfflineAccountId()
      if (!account || !await readOfflineEntitlement(account)) throw new Error('A valid local license is required. Sign in online to activate this device.')
      if (!await keyManager.unlockFromLocalCache(password, account, signal)) throw new Error('Could not unlock locally. Use the last password signed in on this device. Older caches need one online sign-in to enable offline password unlock.')
      throwIfAborted(signal)
      try { secureStorage.setLocallyLocked(false) } catch (cause) { keyManager.clear(); throw cause }
      locallyLocked.value = false
      userId.value = account
      lockedReason.value = null
      keySessionState.value = 'unlocked'
      window.dispatchEvent(new CustomEvent('auth:key-session-unlocked'))
    } catch (cause) {
      error.value = cause instanceof Error ? cause.message : String(cause)
      throw cause
    } finally { loading.value = false }
  }

  /**
   * Resets a forgotten password using the recovery code shown once at
   * registration, then signs in with the new password. Does not require
   * (and never asks for) the old password.
   */
  async function resetPasswordWithRecoveryKey(
    username: string,
    recoveryCode: string,
    newPassword: string,
  ): Promise<void> {
    loading.value = true
    error.value = null
    try {
      await resetPasswordWithRecoveryCode(username, recoveryCode, newPassword)
      await signIn(username, newPassword)
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Requests a "forgot username" reminder email for the given address.
   * Deliberately does NOT touch the shared `loading`/`error` state used by
   * sign-in/sign-up — this runs from a small side-form on the login screen
   * that manages its own local pending/error state, so it doesn't visually
   * fight with an in-flight Argon2id derivation elsewhere on that screen.
   * Always resolves the same way whether or not the email matches an
   * account — see authService.requestUsernameReminder.
   */
  async function requestUsernameReminder(email: string): Promise<void> {
    await requestUsernameReminderRequest(email)
  }

  /**
   * Changes the account password while signed in. Requires the CURRENT
   * password (never the recovery code). Re-wraps the existing MEK under a
   * KEK derived from the new password — never generates a new MEK, so
   * existing per-device key caches stay valid. Revokes every other device's
   * refresh token, routing them into the existing locked/"re-enter password"
   * state (KeySessionUnlockModal.vue / settings/pages/AccountPage.vue).
   */
  async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
    if (!accessToken.value) {
      throw new Error('Not signed in.')
    }
    loading.value = true
    error.value = null
    try {
      const result = await changePasswordRequest(currentPassword, newPassword, accessToken.value)
      await secureStorage.setKdfNonce(result.kdfNonce)
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Changes the account username while signed in. Still requires the CURRENT
   * password, even though only the username is changing — the username is
   * baked into the Argon2id salt, so this is cryptographically a full rekey
   * (see keyManager.ts). Updates secureStorage on THIS device; other
   * signed-in devices are routed into the existing locked/"re-enter password"
   * state by the refresh-token revocation — no separate mechanism needed there.
   */
  async function changeUsername(currentPassword: string, newUsername: string): Promise<void> {
    if (!accessToken.value) {
      throw new Error('Not signed in.')
    }
    loading.value = true
    error.value = null
    try {
      const result = await changeUsernameRequest(currentPassword, newUsername, accessToken.value)
      currentUsername.value = result.username
      await secureStorage.setUsername(result.username)
      await secureStorage.setKdfNonce(result.kdfNonce)
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
      throw e
    } finally {
      loading.value = false
    }
  }

  /** Generates a new one-time recovery code and invalidates the old one. */
  async function generateNewRecoveryCode(currentPassword: string): Promise<string> {
    if (!accessToken.value) {
      throw new Error('Not signed in.')
    }
    loading.value = true
    error.value = null
    try {
      return await rotateRecoveryCodeRequest(currentPassword, accessToken.value)
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Proactively refreshes the access token.
   * Called automatically every 12 minutes by the background interval.
   * Can also be called manually before making authenticated requests.
   */
  async function performRefreshToken(): Promise<string | null> {
    const signal = sessionRequests.signal
    try {
      const res = await refreshAccessToken(refreshToken.value ?? undefined, signal)
      throwIfAborted(signal)
      if (!res || typeof res.accessToken !== 'string' || !res.accessToken.trim()) {
        throw new Error('Malformed refresh response')
      }
      const owner = await secureStorage.getLocalAccountId() ?? userId.value ?? (await getPlatformBridge()?.readKeyStore?.())?.accountId
      if (res.userId && owner && res.userId !== owner) throw new AuthHttpError('Stored cloud session belongs to another account', 403, 'account_mismatch')
      if (res.sessionId) await secureStorage.setSessionId(res.sessionId)
      throwIfAborted(signal)
      accessToken.value = res.accessToken
      lockedReason.value = null
      if (res.sessionId) currentSessionId.value = res.sessionId
      if (res.userId) userId.value = res.userId
      if (res.workspaceId) workspaceId.value = res.workspaceId
      syncEngine.setAuthToken(res.accessToken)
      if (res.userId) syncEngine.setUserId(res.userId)
      if (res.workspaceId) syncEngine.setWorkspaceId(res.workspaceId)
      renewCertificate(res.accessToken, res.sessionId, res.userId, signal)
      return res.accessToken
    } catch (e: any) {
      if (signal.aborted) return null
      if (isCloudSessionRejection(e)) {
        if (e.reason === 'account_deleted') {
          await _markAccountDeleted()
        } else {
          await _disconnectCloudSession(e.reason)
        }
      } else {
        console.warn('[AuthStore] Refresh token network blip or temporary error:', e)
      }
      return null
    }
  }

  async function doRefreshToken(): Promise<string | null> {
    if (storedSessionRejected) return null
    if (tokenRefreshPromise) return tokenRefreshPromise

    const task = performRefreshToken()
    tokenRefreshPromise = task
    try {
      return await task
    } finally {
      if (tokenRefreshPromise === task) tokenRefreshPromise = null
    }
  }

  // ── Session & Device Management Actions ─────────────────────────────────────

  /**
   * Fetches active sessions from the server and tags the current session.
   */
  async function fetchSessions(): Promise<DeviceSession[]> {
    if (!accessToken.value) {
      sessions.value = []
      return []
    }
    loadingSessions.value = true
    try {
      const list = await listSessions(accessToken.value, {
        sessionId: currentSessionId.value ?? undefined,
        refreshToken: refreshToken.value ?? undefined,
      })
      sessions.value = list.map((s) => ({
        ...s,
        isCurrentSession: Boolean(s.isCurrentSession || (currentSessionId.value && s.id === currentSessionId.value)),
      }))
      return sessions.value
    } catch (err) {
      console.warn('[AuthStore] Failed to fetch sessions:', err)
      throw err
    } finally {
      loadingSessions.value = false
    }
  }

  /**
   * Revokes an individual session. If revoking the current session, signs out locally.
   * Otherwise optimistically removes the revoked session from the list.
   */
  async function revokeSession(id: string): Promise<void> {
    if (!accessToken.value) return
    const isCurrent = Boolean(
      (currentSessionId.value && id === currentSessionId.value) ||
      sessions.value.find((s) => s.id === id)?.isCurrentSession
    )
    await revokeSessionRequest(id, accessToken.value)
    if (isCurrent) {
      await signOut()
    } else {
      sessions.value = sessions.value.filter((s) => s.id !== id)
    }
  }

  /**
   * Renames a device session inline.
   */
  async function renameSession(id: string, newLabel: string): Promise<void> {
    if (!accessToken.value) return
    const updatedLabel = await renameSessionRequest(id, newLabel, accessToken.value)
    const target = sessions.value.find((s) => s.id === id)
    if (target) {
      target.deviceLabel = updatedLabel
    }
    if (target?.isCurrentSession || (currentSessionId.value && id === currentSessionId.value)) {
      await secureStorage.setDeviceLabel(updatedLabel)
    }
  }

  // ── Internal helpers ───────────────────────────────────────────────────────

  /** The remembered username of the account whose writing is here, unless it matches the one signing in. */
  function otherAccountName(signingIn?: string): string | null {
    const held = currentUsername.value
    return held && held.toLowerCase() !== signingIn?.trim().toLowerCase() ? held : null
  }

  async function _applySession(
    token: string,
    refresh: string,
    uid: string,
    wsId?: string,
    sessId?: string,
    preparedKeys?: import('../services/keyManager').PersistedKeyStore,
    username?: string,
    deviceLabel?: string,
  ): Promise<void> {
    const signal = sessionRequests.signal
    await recoverCredentialTransaction()
    const cachedKeyAccount = (await getPlatformBridge()?.readKeyStore?.())?.accountId
    const owner = await secureStorage.getLocalAccountId() ?? await readOfflineAccountId() ?? cachedKeyAccount
    if ((owner && owner !== uid) || (cachedKeyAccount && cachedKeyAccount !== uid)) {
      throw new OtherAccountWritingError(otherAccountName(username))
    }
    if (!preparedKeys || preparedKeys.accountId !== uid) throw new Error('The prepared encryption keys do not belong to this account.')
    if (keyManager.isUnlocked() && !await verifyKeyCanary(keyManager.getMasterKey()!, preparedKeys.canary)) throw new Error('The server encryption key does not match this device’s unlocked writing. Export your local writing before replacing its keys.')
    const entitlement = await prepareEntitlement(token, sessId, { signal, accountId: uid })
    let resolvedWsId = wsId ?? null
    if (!resolvedWsId && token) {
      try {
        const data = await withRequestDeadline(async (requestSignal) => {
          const res = await fetch(`${syncEngine.getApiUrl()}/api/workspaces`, {
            signal: requestSignal, headers: { Authorization: `Bearer ${token}` },
          })
          return res.ok ? await res.json() as { workspaces: Array<{ id: string }> } : null
        }, { signal })
        if (data?.workspaces?.length) resolvedWsId = data.workspaces[0].id
      } catch {}
    }

    throwIfAborted(signal)
    await commitCredentials(async () => {
      const currentOwner = await secureStorage.getLocalAccountId()
      if (currentOwner && currentOwner !== uid) throw new OtherAccountWritingError(otherAccountName(username))
      await persistEntitlement(entitlement, signal)
      if (keyManager.isUnlocked() && await verifyKeyCanary(keyManager.getMasterKey()!, preparedKeys.canary)) {
        preparedKeys.dekCache = { ...preparedKeys.dekCache, ...await keyManager.exportDekCache() } as typeof preparedKeys.dekCache
      }
      await keyManager.persistPreparedKeyStore(preparedKeys, signal)
      await secureStorage.setLocalAccountId(uid)
      if (username) await secureStorage.setUsername(username)
      if (deviceLabel) await secureStorage.setDeviceLabel(deviceLabel)
      await secureStorage.setKdfNonce(preparedKeys.kdfNonce ?? '')
      await secureStorage.setSessionId(sessId ?? '')
      await secureStorage.setRefreshToken(refresh)
      secureStorage.setCloudState(null)
      secureStorage.setLocallyLocked(false)
    }, signal, async () => {
      // Keep autosaves paused through activation so they cannot restore old metadata.
      throwIfAborted(signal)
      if (!await keyManager.loadFromKeyStoreData(preparedKeys, signal, uid)) throw new Error('Unable to unlock verified encryption keys')
    })
    throwIfAborted(signal)
    keyManager.schedulePersist()
    locallyLocked.value = false
    accountDeleted.value = false
    storedSessionRejected = false
    cloudSessionReason.value = null
    accessToken.value = token
    refreshToken.value = refresh || null
    userId.value = uid
    currentUsername.value = username ?? currentUsername.value
    currentSessionId.value = sessId ?? null
    lockedReason.value = null
    workspaceId.value = resolvedWsId
    syncEngine.setSyncPaidUntil(entitlement.payload.syncUntil !== undefined ? entitlement.payload.syncUntil : entitlement.payload.validUntil ?? null)

    syncEngine.init({
      authToken: token,
      workspaceId: resolvedWsId,
      userId: uid,
    })

    _startRefreshInterval()

    // Automatically sync cloud tree, settings, and outbox
    syncWithCloud().catch((err) => {
      console.warn('[AuthStore] Failed to sync on _applySession:', err)
    })
  }

  function isCloudSessionRejection(error: unknown): error is AuthHttpError {
    return error instanceof AuthHttpError && (error.status === 401 || error.status === 403)
  }

  /** Stop rejected cloud access without locking or deleting licensed local data. */
  async function _disconnectCloudSession(reason?: string): Promise<void> {
    cancelSessionRequests()
    storedSessionRejected = true
    cloudSessionReason.value = typeof reason === 'string' ? reason : 'session_rejected'
    error.value = 'Cloud sign-in expired or was revoked. Your local writing remains available. Sign in to resume sync.'
    _stopRefreshInterval()
    accessToken.value = null
    refreshToken.value = null
    currentSessionId.value = null
    sessions.value = []
    syncEngine.setAuthToken(null)
    syncEngine.disconnectAllDocuments()
    const removals = await Promise.allSettled([secureStorage.clearRefreshToken(), secureStorage.clearSessionId()])
    if (removals.some(result => result.status === 'rejected')) {
      syncLog.record({ direction: 'system', level: 'error', kind: 'credential-storage', message: 'Cloud credentials could not be removed from local storage. Free storage space or allow local storage, then restart. Local writing remains available.' })
    }
  }

  function _startRefreshInterval(): void {
    _stopRefreshInterval()
    // Refresh every 12 minutes — access token expires at 15 min.
    refreshInterval = setInterval(() => { doRefreshToken() }, 12 * 60 * 1000)
  }

  function _stopRefreshInterval(): void {
    if (refreshInterval !== null) {
      clearInterval(refreshInterval)
      refreshInterval = null
    }
  }

  /** True when the current JWT is expired or has less than three minutes left. */
  function accessTokenNeedsRefresh(): boolean {
    const token = accessToken.value
    if (!token) return false
    try {
      const payloadSegment = token.split('.')[1]
      if (!payloadSegment) return true
      const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/')
      const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
      const payload = JSON.parse(atob(padded)) as { exp?: number }
      return typeof payload.exp !== 'number' || payload.exp * 1000 <= Date.now() + 3 * 60 * 1000
    } catch {
      return true
    }
  }

  // ── Automatic reconnect ─────────────────────────────────────────────────────
  // When the app opens without reaching the server (offline, or the server is
  // down), the keys unlock from the local cache and writing works, but there
  // is no account session, so nothing syncs. Retry the stored session when the
  // network returns, when the window regains focus, and once a minute.

  const RECONNECT_INTERVAL_MS = 60_000
  let reconnectPromise: Promise<boolean> | null = null
  let reconnectInterval: ReturnType<typeof setInterval> | null = null

  function tryReconnect(): Promise<boolean> {
    if (accessToken.value || keySessionState.value !== 'unlocked' || storedSessionRejected || secureStorage.getCloudState()) {
      return Promise.resolve(false)
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) return Promise.resolve(false)
    if (!reconnectPromise) {
      reconnectPromise = restoreSession().finally(() => { reconnectPromise = null })
    }
    return reconnectPromise
  }

  // Retry on a timer only while writing is unlocked without a session.
  watch(
    () => !accessToken.value && keySessionState.value === 'unlocked',
    (disconnected) => {
      if (disconnected && !reconnectInterval) {
        reconnectInterval = setInterval(() => { void tryReconnect() }, RECONNECT_INTERVAL_MS)
      } else if (!disconnected && reconnectInterval) {
        clearInterval(reconnectInterval)
        reconnectInterval = null
      }
    },
    { immediate: true },
  )

  // A renewed sync subscription (picked up by the next certificate renewal)
  // catches up in both directions without the user doing anything.
  watch(() => syncEngine.syncLapsed.value, (lapsed, wasLapsed) => {
    if (wasLapsed && !lapsed && accessToken.value) {
      syncWithCloud().catch((err) => {
        console.warn('[AuthStore] Failed to sync after the subscription was renewed:', err)
      })
    }
  })

  // Any session, however it was established, makes the stored one worth trying again later.
  watch(accessToken, (token) => {
    if (token) {
      storedSessionRejected = false
      cloudSessionReason.value = null
    }
  })

  // Automatic outbox flushes do not pass through syncWithCloud(). When one
  // drains successfully, record the same minimal checkpoint as a manual sync.
  watch(
    () => syncEngine.syncStatus.value,
    (status, previous) => {
      if (status === 'synced' && previous === 'pending' && accessToken.value) {
        void reportSyncCheckpoint(accessToken.value, 'ok')
      }
    },
  )

  // Page text travels through the encrypted Yjs connection rather than the
  // REST outbox. Its acknowledgment is another genuine completed sync.
  watch(
    () => syncEngine.activeDocumentDeliveryStatus.value,
    (status, previous) => {
      if (status === 'synced' && previous !== 'synced' && accessToken.value) {
        void reportSyncCheckpoint(accessToken.value, 'ok')
      }
    },
  )

  function refreshAfterResume(): void {
    if (!accessToken.value) {
      void tryReconnect()
    } else if (accessTokenNeedsRefresh()) {
      void doRefreshToken()
    }
  }

  function reportCredentialStorageFailure(): void {
    error.value = 'Encryption keys could not be saved. Free storage space before closing the app; new documents may not reopen offline.'
    syncLog.record({ direction: 'system', level: 'error', kind: 'credential-storage', message: error.value })
  }

  // Other tabs/windows sharing this profile must honor explicit device actions.
  async function handleCredentialStorageChange(event: StorageEvent): Promise<void> {
    if (event.key === 'superelectric_ink_cloud_state_v1' && secureStorage.getCloudState()) {
      const reason = secureStorage.getCloudState()!
      accountDeleted.value = reason === 'account_deleted'
      await _disconnectCloudSession(reason)
      if (reason === 'signed_out') error.value = null
    }
    if (event.key === 'superelectric_ink_local_lock_v1' && secureStorage.isLocallyLocked() && !locallyLocked.value) {
      let resume = () => {}
      try {
        resume = await prepareLocalSessionClose()
        if (keyManager.isUnlocked() && !await keyManager.saveToLocalCache()) throw new Error('Local keys could not be saved')
        await _disconnectCloudSession(secureStorage.getCloudState() ?? 'signed_out')
        locallyLocked.value = true
        keyManager.clear()
        keySessionState.value = 'locked'
        lockedReason.value = 'local_lock'
        error.value = null
      } catch {
        error.value = 'Another window requested a local lock, but this window could not save its writing or keys. Free storage space and lock this window again.'
      } finally { resume() }
    }
  }

  function refreshOnVisibility(): void {
    if (document.visibilityState === 'visible') refreshAfterResume()
  }

  // A 401 from any SyncEngine REST request can now self-heal without creating
  // a circular module import. Refresh proactively too when a suspended browser
  // tab becomes active again, before the user's next edit or manual sync.
  syncEngine.setAuthRefreshHandler(doRefreshToken)
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    window.addEventListener('storage', handleCredentialStorageChange)
    window.addEventListener('auth:storage-error', reportCredentialStorageFailure)
    window.addEventListener('focus', refreshAfterResume)
    window.addEventListener('online', refreshAfterResume)
    document.addEventListener('visibilitychange', refreshOnVisibility)
  }

  onScopeDispose(() => {
    sessionRequests.abort()
    _stopRefreshInterval()
    if (reconnectInterval) clearInterval(reconnectInterval)
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', handleCredentialStorageChange)
      window.removeEventListener('auth:storage-error', reportCredentialStorageFailure)
      window.removeEventListener('focus', refreshAfterResume)
      window.removeEventListener('online', refreshAfterResume)
      document.removeEventListener('visibilitychange', refreshOnVisibility)
    }
  })

  // ── Expose ─────────────────────────────────────────────────────────────────

  return {
    // State
    accessToken,
    refreshToken,
    currentSessionId,
    sessions,
    loadingSessions,
    userId,
    workspaceId,
    username: currentUsername,
    keySessionState,
    lockedReason,
    cloudSessionReason,
    accountDeleted,
    locallyLocked,
    loading,
    error,
    // Computed
    isAuthenticated,
    isKeySessionUnlocked,
    // Actions
    signIn,
    signOut,
    lockKeySession,
    stopSync,
    resumeSync,
    restoreSession,
    restoreKeySession,
    bootstrapSession,
    unlockKeySession,
    resetPasswordWithRecoveryKey,
    requestUsernameReminder,
    changePassword,
    changeUsername,
    generateNewRecoveryCode,
    syncWithCloud,
    /** Manually trigger an access token refresh. */
    doRefreshToken,
    fetchSessions,
    revokeSession,
    renameSession,
    deleteCloudAccount,
    removeLocalData,
    removeDeletedAccountFromDevice: removeLocalData,
  }
})
